/**
 * ReservationConfirmation — intermediate premium page before opening the
 * affiliate provider link. Keeps the client inside the Escapafy ecosystem
 * with a branded summary of the item they are about to book.
 *
 * URL: /reserva/:token?block=<blockType>&item=<itemId>
 *
 * On mount:
 *   1. Fetches the proposal via publicApi.getProposal(token).
 *   2. Locates the block by `type` and the item by `id` (fallback: `__idx_N`).
 *   3. Renders logo + product image + name + dates + price + description.
 *   4. On CTA click, opens `affiliate_url` in a NEW TAB so the user can
 *      easily return to the Escapafy proposal.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Calendar, MapPin, Clock, ShieldCheck } from "lucide-react";
import { publicApi, formatApiError, API } from "./api";
import { LOGO_URL } from "../data/escapafy";

// ── Helpers (mirror PublicConfigurator to keep display consistent) ────────
const fmt = (n) =>
  new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(n || 0);

const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v).replace(",", "."));
  return Number.isFinite(n) && n >= 0 ? n : 0;
};

const absoluteUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u}`;
};

const itemImages = (item) => {
  const arr = Array.isArray(item.images) ? item.images.filter(Boolean) : [];
  if (arr.length) return arr.map(absoluteUrl);
  if (item.image_url) return [absoluteUrl(item.image_url)];
  return [];
};

const itemAmount = (item) => {
  const mode = item.pricing_mode || (toNum(item.amount) > 0 ? "included" : "");
  return mode === "included" ? toNum(item.amount) : 0;
};

const BLOCK_LABELS = {
  flights: "Vuelo",
  hotels: "Alojamiento",
  transfers: "Traslado",
  restaurants: "Restaurante",
  activities: "Actividad",
  excursions: "Excursión",
  transport: "Transporte",
  insurance: "Seguro de viaje",
};

const providerFromUrl = (url) => {
  try {
    const u = new URL(url);
    return u.hostname.replace(/^www\./, "");
  } catch {
    return "el proveedor oficial";
  }
};

// ── Item name/dates/description resolvers ────────────────────────────────
function itemDisplayName(item, blockType) {
  if (blockType === "insurance") return item.company || "Seguro de viaje";
  if (blockType === "flights") return item.name || item.airline || `${item.outbound_airline || ""} · ${item.outbound_route || ""}`.trim() || "Vuelo";
  if (blockType === "transfers") return item.type || item.company || "Traslado";
  return item.name || item.airline || item.company || item.type || "Reserva";
}

function itemDates(item, blockType) {
  if (blockType === "flights") {
    const out = item.outbound_date;
    const ret = item.return_date;
    if (out && ret) return `${out} → ${ret}`;
    return out || ret || item.dates || "";
  }
  if (blockType === "hotels") {
    if (item.check_in && item.check_out) return `${item.check_in} → ${item.check_out}${item.nights ? ` · ${item.nights} noches` : ""}`;
    return item.dates || (item.nights ? `${item.nights} noches` : "");
  }
  return item.date || item.dates || item.schedule || "";
}

function itemLocation(item) {
  return item.location || item.address || item.route || item.outbound_route || "";
}

function itemDescription(item) {
  return item.description || item.coverage || item.includes || "";
}

// ── Locate item inside proposal blocks ────────────────────────────────────
function findItem(proposal, blockType, itemIdParam) {
  const block = (proposal.blocks || []).find((b) => b.type === blockType);
  if (!block) return { block: null, item: null };

  // Insurance block stores data directly (not in items[])
  if (blockType === "insurance") {
    return { block, item: block.data || null };
  }

  const items = block.data?.items || [];
  if (!items.length) return { block, item: null };

  // Match by explicit id first
  let item = items.find((it) => it.id && it.id === itemIdParam);
  if (item) return { block, item };

  // Fallback: __idx_N legacy id
  const m = /^__idx_(\d+)$/.exec(itemIdParam || "");
  if (m) {
    const idx = parseInt(m[1], 10);
    if (items[idx]) return { block, item: items[idx] };
  }

  // Booking mode fallback: if only one chosen item, return it
  const chosen = items.filter((it) => it.is_chosen === true);
  if (chosen.length === 1) return { block, item: chosen[0] };

  return { block, item: null };
}

// ── Main ──────────────────────────────────────────────────────────────────
export default function ReservationConfirmation() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const blockType = params.get("block") || "";
  const itemIdParam = params.get("item") || "";

  const [proposal, setProposal] = useState(null);
  const [error, setError] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    if (!token) return;
    publicApi
      .getProposal(token)
      .then(setProposal)
      .catch((err) => setError(formatApiError(err)));
  }, [token]);

  const { block, item } = useMemo(
    () => (proposal ? findItem(proposal, blockType, itemIdParam) : { block: null, item: null }),
    [proposal, blockType, itemIdParam],
  );

  const affiliateUrl = item?.affiliate_url || "";
  const provider = affiliateUrl ? providerFromUrl(affiliateUrl) : "";
  const backHref = `/p/${token}`;

  // Fire-and-forget click tracking when user confirms
  const handleConfirm = () => {
    if (!affiliateUrl) return;
    setRedirecting(true);
    try {
      fetch(`${API}/public/proposals/${token}/click-track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          block_type: blockType,
          item_name: itemDisplayName(item || {}, blockType),
          url: affiliateUrl,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* noop */ }
    // Open provider in a NEW TAB so the user can return to Escapafy easily
    window.open(affiliateUrl, "_blank", "noopener,noreferrer");
    // Small delay to reset button state in case user navigates back
    setTimeout(() => setRedirecting(false), 1500);
  };

  // ── Loading / error states ──
  if (error) {
    return (
      <Shell>
        <div className="max-w-lg text-center">
          <h1 className="font-serif italic text-3xl text-[#0A192F]">No hemos podido cargar tu reserva</h1>
          <p className="text-[#3A506B] mt-3 text-sm">{error}</p>
          <Link to={backHref} className="mt-8 inline-flex items-center gap-2 text-[#0A192F] underline underline-offset-4 text-sm">
            <ArrowLeft size={14} /> Volver a la propuesta
          </Link>
        </div>
      </Shell>
    );
  }
  if (!proposal) {
    return (
      <Shell>
        <p className="text-[#3A506B] italic">Preparando tu reserva…</p>
      </Shell>
    );
  }
  if (!item) {
    return (
      <Shell>
        <div className="max-w-lg text-center">
          <h1 className="font-serif italic text-3xl text-[#0A192F]">Elemento no encontrado</h1>
          <p className="text-[#3A506B] mt-3 text-sm">No hemos podido localizar este elemento en tu propuesta. Vuelve a la propuesta e inténtalo de nuevo.</p>
          <Link to={backHref} className="mt-8 inline-flex items-center gap-2 text-[#0A192F] underline underline-offset-4 text-sm">
            <ArrowLeft size={14} /> Volver a la propuesta
          </Link>
        </div>
      </Shell>
    );
  }

  // ── Content ──
  const label = BLOCK_LABELS[blockType] || "Reserva";
  const name = itemDisplayName(item, blockType);
  const dates = itemDates(item, blockType);
  const location = itemLocation(item);
  const description = itemDescription(item);
  const price = itemAmount(item);
  const imgs = itemImages(item);
  const heroImage = imgs[0];

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#0A192F]" data-testid="reservation-confirmation">
      {/* Top bar with logo + back link */}
      <header className="border-b border-[#EAE3D5] bg-[#FDFBF7]/95 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-28 flex items-center justify-between">
          <Link to={backHref} data-testid="back-to-proposal-link" className="inline-flex items-center gap-2 text-[#3A506B] hover:text-[#0A192F] text-xs tracking-[0.2em] uppercase transition-colors">
            <ArrowLeft size={14} /> Volver a la propuesta
          </Link>
          <img src={LOGO_URL} alt="Escapafy" style={{ height: "100px", width: "auto" }} />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-12 lg:py-20">
        {/* Eyebrow */}
        <p className="text-[#E8C97A] text-[0.65rem] tracking-[0.35em] uppercase font-semibold mb-4" data-testid="reservation-eyebrow">
          Confirma tu reserva · {label}
        </p>
        <h1 className="font-serif italic text-4xl sm:text-5xl leading-tight text-[#0A192F] max-w-3xl">
          {name}
        </h1>

        {/* Summary card */}
        <section
          className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 md:gap-10 bg-white border border-[#EAE3D5] overflow-hidden"
          style={{ borderRadius: "4px" }}
          data-testid="reservation-summary-card"
        >
          {heroImage ? (
            <div className="w-full aspect-[4/3] md:aspect-auto md:min-h-[320px] bg-[#F3EFE7]">
              <img
                src={heroImage}
                alt={name}
                className="w-full h-full object-cover"
                data-testid="reservation-item-image"
              />
            </div>
          ) : (
            <div className="w-full aspect-[4/3] md:aspect-auto md:min-h-[320px] bg-[#F3EFE7] flex items-center justify-center">
              <img src={LOGO_URL} alt="Escapafy" style={{ height: "44px", width: "auto", opacity: 0.35 }} />
            </div>
          )}

          <div className="p-7 lg:p-10 flex flex-col">
            <p className="text-[#3A506B] text-[0.65rem] tracking-[0.25em] uppercase mb-2">{label}</p>
            <h2 className="font-configurator text-2xl text-[#0A192F]" data-testid="reservation-item-name">{name}</h2>

            <ul className="mt-5 space-y-3 text-sm text-[#3A506B]">
              {location && (
                <li className="inline-flex items-start gap-2">
                  <MapPin size={14} className="mt-0.5 text-[#E8C97A] shrink-0" />
                  <span data-testid="reservation-location">{location}</span>
                </li>
              )}
              {dates && (
                <li className="inline-flex items-start gap-2">
                  <Calendar size={14} className="mt-0.5 text-[#E8C97A] shrink-0" />
                  <span data-testid="reservation-dates">{dates}</span>
                </li>
              )}
              {item.duration && (
                <li className="inline-flex items-start gap-2">
                  <Clock size={14} className="mt-0.5 text-[#E8C97A] shrink-0" />
                  <span>{item.duration}</span>
                </li>
              )}
            </ul>

            {description && (
              <p className="mt-5 text-[#3A506B] text-sm leading-relaxed whitespace-pre-wrap">{description}</p>
            )}

            {price > 0 && (
              <div className="mt-7 pt-6 border-t border-[#EAE3D5]">
                <p className="text-[#3A506B] text-[0.65rem] tracking-[0.25em] uppercase">Precio orientativo</p>
                <p className="font-configurator text-3xl text-[#0A192F] font-light mt-1" data-testid="reservation-price">{fmt(price)}</p>
                <p className="text-[0.7rem] text-[#3A506B]/80 mt-2 italic">
                  Precio de referencia. El importe final se confirma en el proveedor.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Redirect notice */}
        <section className="mt-10 max-w-3xl">
          <div className="flex items-start gap-3 bg-[#F3EFE7] border border-[#EAE3D5] p-5" style={{ borderRadius: "4px" }}>
            <ShieldCheck size={18} className="text-[#E8C97A] mt-0.5 shrink-0" />
            <p className="text-[#3A506B] text-sm leading-relaxed">
              Vas a ser redirigido{provider ? <> a <strong className="text-[#0A192F]">{provider}</strong></> : null} para completar la reserva con el proveedor oficial.
              Se abrirá en una pestaña nueva para que puedas volver fácilmente a tu propuesta de Escapafy si necesitas consultar otra información.
            </p>
          </div>
        </section>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          {affiliateUrl ? (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={redirecting}
              data-testid="continue-reservation-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#0A192F] hover:bg-[#16335A] disabled:opacity-60 text-[#FDFBF7] px-10 py-5 text-sm tracking-[0.2em] uppercase font-medium transition-colors shadow-[0_12px_36px_rgba(10,25,47,0.18)]"
              style={{ borderRadius: "2px" }}
            >
              {redirecting ? "Abriendo…" : "Continuar con la reserva"}
              <ArrowUpRight size={15} />
            </button>
          ) : (
            <p className="text-[#3A506B] text-sm italic" data-testid="reservation-no-link">
              Enlace de reserva pendiente — contacta con nosotros para completar esta reserva.
            </p>
          )}
          <Link
            to={backHref}
            className="text-[#3A506B] hover:text-[#0A192F] text-xs tracking-[0.2em] uppercase underline underline-offset-4 transition-colors"
          >
            Volver a la propuesta
          </Link>
        </div>

        {/* Footer copy */}
        <p className="mt-16 text-[#3A506B] text-xs italic max-w-2xl leading-relaxed">
          La reserva se realiza directamente con el proveedor oficial para garantizar las mejores condiciones,
          políticas de cancelación y atención al cliente. Escapafy se encarga de la planificación y coordinación de tu viaje.
        </p>
      </main>
    </div>
  );
}

// ── Shared shell (loading / error) ──
function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col">
      <header className="border-b border-[#EAE3D5]">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-28 flex items-center justify-between">
          <span className="text-[#3A506B] text-xs tracking-[0.2em] uppercase">Reserva</span>
          <img src={LOGO_URL} alt="Escapafy" style={{ height: "100px", width: "auto" }} />
        </div>
      </header>
      <div className="flex-1 flex items-center justify-center p-6">
        {children}
      </div>
    </div>
  );
}
